import { defineStore } from 'pinia'
import { ref, computed } from '@vue/composition-api'
import { getAllUser } from '@/model/dataLayer/api/MembershipSystem/UserMembership'

export const useMemberSelectorStore = defineStore('member-selector', () => {
  // State
  const members = ref([])
  const filteredMembers = ref([])
  const selectedMemberIds = ref([])
  const isLoading = ref(false)
  const searchQuery = ref('')
  const selectedLabels = ref([])
  const ageFilter = ref('')
  const genderFilter = ref('')

  // Getters
  const getFilteredMembers = computed(() => {
    return Array.isArray(filteredMembers.value) ? filteredMembers.value : []
  })

  const getSelectedMembers = computed(() => {
    return Array.isArray(members.value)
      ? members.value.filter(member => selectedMemberIds.value.includes(member.currentMembership.id))
      : []
  })

  // Actions
  async function fetchMembers () {
    try {
      isLoading.value = true
      const result = await getAllUser()
      members.value = result
      applyFilters()
    } catch (error) {
      console.error('Error fetching members:', error)
    } finally {
      isLoading.value = false
    }
  }

  function applyFilters () {
    // Ensure members.value is an array before spreading
    let result = Array.isArray(members.value) ? [...members.value] : []

    // Filter by search query (name, username, email, phone)
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      result = result.filter(member =>
        (member.username && member.username.toLowerCase().includes(query)) ||
        (member.email && member.email.toLowerCase().includes(query)) ||
        (member.phoneNumber && member.phoneNumber.toLowerCase().includes(query))
      )
    }

    // Filter by labels
    if (selectedLabels.value.length > 0) {
      result = result.filter(member => {
        if (!member.labels || !Array.isArray(member.labels)) return false
        return selectedLabels.value.some(labelId =>
          member.labels.some(label => label && label.id === labelId)
        )
      })
    }

    // Filter by age
    if (ageFilter.value) {
      result = result.filter(member =>
        member.ageDisplay === ageFilter.value
      )
    }

    // Filter by gender
    if (genderFilter.value) {
      result = result.filter(member =>
        member.gender === genderFilter.value
      )
    }

    filteredMembers.value = result
  }

  function setSearchQuery (query) {
    searchQuery.value = query
    applyFilters()
  }

  function setSelectedLabels (labels) {
    selectedLabels.value = labels
    applyFilters()
  }

  function setAgeFilter (age) {
    ageFilter.value = age
    applyFilters()
  }

  function setGenderFilter (gender) {
    genderFilter.value = gender
    applyFilters()
  }

  function selectMember (memberId) {
    if (!selectedMemberIds.value.includes(memberId)) {
      selectedMemberIds.value.push(memberId)
    }
  }

  function unselectMember (memberId) {
    selectedMemberIds.value = selectedMemberIds.value.filter(id => id !== memberId)
  }

  function toggleMemberSelection (memberId) {
    // Find the member by ID
    const member = members.value.find(m => m.id === memberId)
    if (!member || !member.currentMembership) return

    // Use the membership ID instead of the user ID
    const membershipId = member.currentMembership.id

    if (selectedMemberIds.value.includes(membershipId)) {
      unselectMember(membershipId)
    } else {
      selectMember(membershipId)
    }
  }

  function selectMembers (memberIds) {
    selectedMemberIds.value = [...memberIds]
  }

  function clearSelection () {
    selectedMemberIds.value = []
  }

  function selectAll () {
    // Select all filtered members
    if (Array.isArray(filteredMembers.value)) {
      selectedMemberIds.value = filteredMembers.value
        .filter(member => member.currentMembership && member.currentMembership.id)
        .map(member => member.currentMembership.id)
    }
  }

  function resetFilters () {
    searchQuery.value = ''
    selectedLabels.value = []
    ageFilter.value = ''
    genderFilter.value = ''
    applyFilters()
  }

  // Function to return selected member IDs via Promise
  function getSelectedMemberIds (returnMode = 'membership') {
    return new Promise((resolve) => {
      if (returnMode === 'membership') {
        // Return membership IDs (current behavior)
        resolve([...selectedMemberIds.value])
      } else if (returnMode === 'user') {
        // Return user IDs
        const selectedMembers = members.value.filter(member =>
          member.currentMembership && selectedMemberIds.value.includes(member.currentMembership.id)
        )
        resolve(selectedMembers.map(member => member.id))
      } else {
        // Default to membership IDs
        resolve([...selectedMemberIds.value])
      }
    })
  }

  return {
    // State
    members,
    filteredMembers,
    selectedMemberIds,
    isLoading,
    searchQuery,
    selectedLabels,
    ageFilter,
    genderFilter,

    // Getters
    getFilteredMembers,
    getSelectedMembers,

    // Actions
    fetchMembers,
    applyFilters,
    setSearchQuery,
    setSelectedLabels,
    setAgeFilter,
    setGenderFilter,
    selectMember,
    unselectMember,
    toggleMemberSelection,
    selectMembers,
    clearSelection,
    selectAll,
    resetFilters,
    getSelectedMemberIds
  }
})
